import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  AlertTriangle, CheckCircle2, FileText, Eye, Sparkles, RefreshCw, Send, X, Search,
  ShieldAlert, ShieldCheck, FileQuestion, Loader2, ChevronDown, ExternalLink, Pencil,
  Inbox, Rocket, Newspaper, AlertOctagon, NotebookPen, CircleDot,
} from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { usePublicArticlesEnabled } from "@/hooks/usePublicArticlesEnabled";
import { computeArticleSeo, SEO_DOT, type SeoCheck } from "@/lib/articleSeo";

type ArticleStatus = "draft" | "needs_review" | "approved" | "published" | "rejected";
type Article = {
  id: string;
  pick_id: string;
  sport: string | null;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  excerpt: string | null;
  article_body_md: string | null;
  article_body_html: string | null;
  status: ArticleStatus;
  validation_error: string | null;
  pick_snapshot: any;
  ai_generated_at: string | null;
  reviewed_at: string | null;
  created_at: string;
  published_at: string | null;
};

type Pick = {
  id: string;
  title: string;
  teams: string | null;
  bet_type: string;
  status: string;
  confidence_score: number;
  game_start_time: string | null;
  sport_id: string;
  sports?: { code: string } | null;
};

type Row = { pick: Pick; article: Article | null };

const STATUS_META: Record<string, { label: string; cls: string; icon: any }> = {
  none: { label: "No Article", cls: "bg-muted/40 text-muted-foreground border-border", icon: FileQuestion },
  draft: { label: "Draft", cls: "bg-muted/60 text-foreground border-border", icon: FileText },
  needs_review: { label: "Needs Review", cls: "bg-accent/15 text-accent border-accent/30", icon: Eye },
  approved: { label: "Approved", cls: "bg-primary/15 text-primary border-primary/30 shadow-[0_0_12px_hsl(var(--primary)/0.25)]", icon: ShieldCheck },
  published: { label: "Published", cls: "bg-primary/20 text-primary border-primary/40 shadow-[0_0_18px_hsl(var(--primary)/0.35)]", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-destructive/15 text-destructive border-destructive/30", icon: X },
  validation_error: { label: "Validation Error", cls: "bg-destructive/15 text-destructive border-destructive/40", icon: ShieldAlert },
};

const REQUIRED_ARTICLE_HEADERS = [
  "Quick Matchup Snapshot", "Matchup Overview", "Market Snapshot", "Recent Form",
  "Injury / Lineup Notes", "Risk Factors", "AleaEdge Lean", "Locked Premium Section", "Disclaimer",
];
const PREMIUM_TEASER = "Unlock the full AleaEdge breakdown to see the complete market edge, confidence matrix, unit rating, and premium reasoning.";
const DISCLAIMER = "This analysis is for informational and entertainment purposes only. No outcome is guaranteed.";
const FORBIDDEN_PROMO_WORDING = [/\block\b/i, /\bguaranteed\b/i, /\brisk-free\b/i, /can[’']t miss/i, /\bsure thing\b/i];

function validateArticleDraft(md: string) {
  const errs: string[] = [];
  if (!/^#\s+.+Prediction\s*&\s*Betting\s*Preview\s*[—–-]\s*(\d{4}-\d{2}-\d{2}|[A-Za-z]+\s+\d{1,2},\s*\d{4})/m.test(md)) {
    errs.push("Missing/invalid H1 format");
  }
  for (const header of REQUIRED_ARTICLE_HEADERS) {
    const safe = header.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (!new RegExp(`^#{2,3}\\s+${safe}`, "m").test(md)) errs.push(`Missing section: ${header}`);
  }
  if (!md.includes(PREMIUM_TEASER)) errs.push("Missing premium teaser sentence");
  if (!md.includes(DISCLAIMER)) errs.push("Missing required disclaimer sentence");
  const scanText = md.replace(/Locked Premium Section/gi, "").replace(DISCLAIMER, "");
  if (FORBIDDEN_PROMO_WORDING.some((w) => w.test(scanText))) errs.push("Contains forbidden wording");
  return errs;
}

function StatusBadge({ status, hasValidation }: { status: string; hasValidation?: boolean }) {
  const key = hasValidation ? "validation_error" : status;
  const m = STATUS_META[key] || STATUS_META.none;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${m.cls}`}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function fmtTime(d: string | null | undefined) {
  if (!d) return "";
  return new Date(d).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function isToday(d: string | null | undefined) {
  if (!d) return false;
  const x = new Date(d), n = new Date();
  return x.getFullYear() === n.getFullYear() && x.getMonth() === n.getMonth() && x.getDate() === n.getDate();
}

// SEO scoring is centralized in src/lib/articleSeo.ts
function computeSeo(article: Article | null, allArticles: Article[]) {
  return computeArticleSeo(article as any, allArticles as any);
}

function SeoChip({ check }: { check: SeoCheck }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background/40 px-1.5 py-0.5 text-[10px] text-muted-foreground"
      title={`${check.label}: ${check.hint ?? ""}${check.fix ? ` — ${check.fix}` : ""}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${SEO_DOT[check.level]}`} />
      {check.label}
    </span>
  );
}

// ---------------- Page ----------------
export default function AdminArticles() {
  const [picks, setPicks] = useState<Pick[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openArticle, setOpenArticle] = useState<Article | null>(null);
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null);
  const [confirmPublish, setConfirmPublish] = useState<{ pick: Pick; article: Article } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const publicArticles = usePublicArticlesEnabled();

  // Filters
  const [search, setSearch] = useState("");
  const [sportFilter, setSportFilter] = useState<string>("all");
  const [pickStatusFilter, setPickStatusFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");

  async function load() {
    setLoading(true);
    const [picksRes, artsRes] = await Promise.all([
      supabase.from("picks")
        .select("id,title,teams,bet_type,status,confidence_score,game_start_time,sport_id,sports(code)")
        .is("deleted_at", null).order("created_at", { ascending: false }).limit(200),
      supabase.from("pick_articles" as any).select("*").order("created_at", { ascending: false }).limit(500),
    ]);
    setPicks((picksRes.data as any) ?? []);
    setArticles(((artsRes.data as any) ?? []) as Article[]);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const rows: Row[] = useMemo(() => picks.map((p) => ({
    pick: p, article: articles.find((a) => a.pick_id === p.id) ?? null,
  })), [picks, articles]);

  const sports = useMemo(() => Array.from(new Set(picks.map((p) => p.sports?.code).filter(Boolean))) as string[], [picks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    return rows.filter(({ pick, article }) => {
      if (sportFilter !== "all" && pick.sports?.code !== sportFilter) return false;
      if (pickStatusFilter !== "all" && pick.status !== pickStatusFilter) return false;
      if (dateFilter !== "all") {
        const d = pick.game_start_time ? new Date(pick.game_start_time).getTime() : 0;
        const days = dateFilter === "7d" ? 7 : dateFilter === "30d" ? 30 : 1;
        if (Math.abs(now - d) > days * 86400000) return false;
      }
      if (q) {
        const hay = `${pick.title} ${pick.teams ?? ""} ${pick.bet_type} ${article?.title ?? ""} ${article?.slug ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, sportFilter, pickStatusFilter, dateFilter]);

  // Workflow groups
  const groups = useMemo(() => {
    const g: Record<string, Row[]> = {
      needs_review: [], ready: [], published_today: [], validation: [], drafts: [], no_article: [], other: [],
    };
    for (const r of filtered) {
      const a = r.article;
      if (!a) g.no_article.push(r);
      else if (a.validation_error) g.validation.push(r);
      else if (a.status === "needs_review") g.needs_review.push(r);
      else if (a.status === "approved") g.ready.push(r);
      else if (a.status === "published" && isToday(a.published_at)) g.published_today.push(r);
      else if (a.status === "draft") g.drafts.push(r);
      else g.other.push(r);
    }
    return g;
  }, [filtered]);

  const seoReadyCount = useMemo(
    () => articles.filter((a) => !a.validation_error && computeSeo(a, articles).score >= 75).length,
    [articles],
  );

  // ---- mutations ----
  async function generate(pickId: string, regen = false) {
    setBusyId(pickId);
    const t = toast.loading(regen ? "Regenerating article…" : "Generating article…");
    try {
      const { error } = await supabase.functions.invoke("generate_pick_article", { body: { pick_id: pickId } });
      if (error) throw error;
      toast.success(regen ? "Article regenerated" : "Article drafted", { id: t });
      await load();
    } catch (e: any) { toast.error(e.message ?? "Failed to generate article", { id: t }); }
    finally { setBusyId(null); }
  }
  async function setStatus(article: Article, status: ArticleStatus) {
    setBusyId(article.id);
    const patch: any = { status };
    if (status === "approved") patch.reviewed_at = new Date().toISOString();
    const { error } = await supabase.from("pick_articles" as any).update(patch).eq("id", article.id);
    if (error) toast.error(error.message); else toast.success(`Article ${status.replace("_", " ")}`);
    setBusyId(null); await load();
  }
  async function publishPair(pick: Pick, article: Article) {
    setBusyId(article.id);
    const t = toast.loading("Publishing pick + article…");
    try {
      const { error } = await supabase.functions.invoke("publish_pick_with_article", {
        body: { pick_id: pick.id, article_id: article.id },
      });
      if (error) throw error;
      toast.success("Published", { id: t });
      setConfirmPublish(null); await load();
    } catch (e: any) { toast.error(e.message ?? "Publish failed", { id: t }); }
    finally { setBusyId(null); }
  }
  async function saveEdits(article: Article, updates: Partial<Article>) {
    const nextBody = updates.article_body_md ?? article.article_body_md ?? "";
    const validationErrors = validateArticleDraft(nextBody);
    const patch: Partial<Article> = {
      ...updates,
      article_body_html: updates.article_body_md ? `<pre>${updates.article_body_md.replace(/</g, "&lt;")}</pre>` : updates.article_body_html,
      validation_error: validationErrors.length ? validationErrors.join("; ") : null,
      status: !validationErrors.length && article.status === "draft" ? "needs_review" : updates.status ?? article.status,
    };
    const { error } = await supabase.from("pick_articles" as any).update(patch).eq("id", article.id);
    if (error) { toast.error(error.message); return; }
    toast.success(validationErrors.length ? "Saved with validation issues" : "Article validated and saved");
    await load();
    const fresh = (await supabase.from("pick_articles" as any).select("*").eq("id", article.id).maybeSingle()).data as any;
    if (fresh) setOpenArticle(fresh);
  }

  // ---- bulk actions ----
  function toggleSel(id: string) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function selectGroup(group: Row[]) {
    setSelected((s) => {
      const n = new Set(s);
      const ids = group.map((r) => r.article?.id ?? r.pick.id);
      const allIn = ids.every((id) => n.has(id));
      ids.forEach((id) => allIn ? n.delete(id) : n.add(id));
      return n;
    });
  }
  const selectedRows = useMemo(
    () => filtered.filter((r) => selected.has(r.article?.id ?? r.pick.id)),
    [filtered, selected],
  );
  async function bulkApprove() {
    setBulkBusy(true);
    const targets = selectedRows.filter((r) => r.article && !r.article.validation_error && r.article.status !== "approved" && r.article.status !== "published");
    if (!targets.length) { setBulkBusy(false); return toast.message("No eligible articles selected"); }
    const t = toast.loading(`Approving ${targets.length}…`);
    const { error } = await supabase.from("pick_articles" as any)
      .update({ status: "approved", reviewed_at: new Date().toISOString() })
      .in("id", targets.map((r) => r.article!.id));
    if (error) toast.error(error.message, { id: t }); else toast.success(`${targets.length} approved`, { id: t });
    setSelected(new Set()); setBulkBusy(false); await load();
  }
  async function bulkArchive() {
    setBulkBusy(true);
    const targets = selectedRows.filter((r) => r.article && r.article.status !== "published");
    if (!targets.length) { setBulkBusy(false); return toast.message("No eligible articles selected"); }
    const t = toast.loading(`Rejecting ${targets.length}…`);
    const { error } = await supabase.from("pick_articles" as any)
      .update({ status: "rejected" }).in("id", targets.map((r) => r.article!.id));
    if (error) toast.error(error.message, { id: t }); else toast.success(`${targets.length} archived`, { id: t });
    setSelected(new Set()); setBulkBusy(false); await load();
  }
  async function bulkRegenerate() {
    setBulkBusy(true);
    const targets = selectedRows.filter((r) => r.article);
    if (!targets.length) { setBulkBusy(false); return toast.message("No eligible articles selected"); }
    const t = toast.loading(`Regenerating ${targets.length}…`);
    let ok = 0;
    for (const r of targets) {
      try {
        const { error } = await supabase.functions.invoke("generate_pick_article", { body: { pick_id: r.pick.id } });
        if (!error) ok++;
      } catch { /* ignore */ }
    }
    toast.success(`Regenerated ${ok}/${targets.length}`, { id: t });
    setSelected(new Set()); setBulkBusy(false); await load();
  }
  async function bulkPublish() {
    setBulkBusy(true);
    const targets = selectedRows.filter((r) => r.article && r.article.status === "approved" && !r.article.validation_error);
    if (!targets.length) { setBulkBusy(false); return toast.message("Select approved articles to publish"); }
    const t = toast.loading(`Publishing ${targets.length}…`);
    let ok = 0;
    for (const r of targets) {
      try {
        const { error } = await supabase.functions.invoke("publish_pick_with_article", {
          body: { pick_id: r.pick.id, article_id: r.article!.id },
        });
        if (!error) ok++;
      } catch { /* ignore */ }
    }
    toast.success(`Published ${ok}/${targets.length}`, { id: t });
    setSelected(new Set()); setBulkBusy(false); await load();
  }

  const summary = [
    { key: "review", label: "Awaiting review", value: groups.needs_review.length, icon: Inbox, tone: "text-accent" },
    { key: "ready", label: "Ready to publish", value: groups.ready.length, icon: Rocket, tone: "text-primary" },
    { key: "today", label: "Published today", value: groups.published_today.length, icon: Newspaper, tone: "text-primary" },
    { key: "val", label: "Validation issues", value: groups.validation.length, icon: AlertOctagon, tone: "text-destructive" },
    { key: "seo", label: "SEO-ready", value: seoReadyCount, icon: ShieldCheck, tone: "text-primary" },
  ];

  return (
    <div className="container py-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
            <Sparkles className="w-3.5 h-3.5 text-primary" /> Editorial command center
          </div>
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
            Article <span className="te-grad-text">Console</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 te-card px-3 py-1.5 border border-border/60">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${publicArticles.enabled ? "text-primary" : "text-accent"}`}>
              Public articles {publicArticles.enabled ? "ON" : "OFF (noindex)"}
            </span>
            <Switch
              checked={publicArticles.enabled}
              disabled={publicArticles.loading}
              onCheckedChange={async (v) => {
                try {
                  await publicArticles.setEnabled(v);
                  toast.success(v ? "Public articles enabled — pages now indexable" : "Public articles disabled — pages set to noindex");
                } catch (e: any) {
                  toast.error(e.message ?? "Failed to update setting");
                }
              }}
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary panel */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
        {summary.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="te-card relative overflow-hidden p-3.5 group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-accent/[0.04] opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{s.label}</div>
                  <div className={`text-2xl font-bold mt-0.5 ${s.tone}`}>{s.value}</div>
                </div>
                <Icon className={`w-5 h-5 ${s.tone} opacity-70`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky toolbar */}
      <div className="sticky top-2 z-30">
        <div className="te-card border border-border/60 backdrop-blur-xl bg-background/70 p-3 flex flex-col gap-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search matchup, team, market, slug…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
            </div>
            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Sport" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sports</SelectItem>
                {sports.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={pickStatusFilter} onValueChange={setPickStatusFilter}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All pick states</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="h-9"><SelectValue placeholder="Date" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any time</SelectItem>
                <SelectItem value="1d">Within 24h</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
              <span className="text-xs text-muted-foreground">
                <b className="text-foreground">{selected.size}</b> selected
              </span>
              <div className="ml-auto flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}><X className="w-3.5 h-3.5" /> Clear</Button>
                <Button size="sm" variant="outline" onClick={bulkRegenerate} disabled={bulkBusy}>
                  {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate
                </Button>
                <Button size="sm" variant="ghost" onClick={bulkArchive} disabled={bulkBusy} className="text-destructive hover:text-destructive">
                  <X className="w-3.5 h-3.5" /> Archive
                </Button>
                <Button size="sm" variant="secondary" onClick={bulkApprove} disabled={bulkBusy}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Approve
                </Button>
                <Button size="sm" onClick={bulkPublish} disabled={bulkBusy}>
                  <Send className="w-3.5 h-3.5" /> Publish
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Workflow groups */}
      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState onRefresh={load} />
      ) : (
        <div className="space-y-3">
          <WorkflowGroup title="Needs Review" tone="accent" rows={groups.needs_review} defaultOpen icon={Inbox}
            allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
            busyId={busyId}
            onGenerate={(p) => generate(p.id, true)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
            onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
            onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          <WorkflowGroup title="Ready to Publish" tone="primary" rows={groups.ready} defaultOpen icon={Rocket}
            allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
            busyId={busyId}
            onGenerate={(p) => generate(p.id, true)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
            onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
            onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          <WorkflowGroup title="Validation Issues" tone="destructive" rows={groups.validation} defaultOpen={groups.validation.length > 0} icon={AlertOctagon}
            allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
            busyId={busyId}
            onGenerate={(p) => generate(p.id, true)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
            onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
            onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          <WorkflowGroup title="Drafts" tone="muted" rows={groups.drafts} icon={NotebookPen}
            allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
            busyId={busyId}
            onGenerate={(p) => generate(p.id, true)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
            onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
            onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          <WorkflowGroup title="Published Today" tone="primary" rows={groups.published_today} icon={Newspaper}
            allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
            busyId={busyId}
            onGenerate={(p) => generate(p.id, true)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
            onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
            onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          <WorkflowGroup title="No Article" tone="muted" rows={groups.no_article} icon={FileQuestion}
            allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
            busyId={busyId}
            onGenerate={(p) => generate(p.id, false)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
            onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
            onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          {groups.other.length > 0 && (
            <WorkflowGroup title="Other" tone="muted" rows={groups.other} icon={CircleDot}
              allArticles={articles} selected={selected} onToggle={toggleSel} onToggleAll={selectGroup}
              busyId={busyId}
              onGenerate={(p) => generate(p.id, true)} onPreview={(a) => setPreviewArticle(a)} onEdit={(a) => setOpenArticle(a)}
              onApprove={(a) => setStatus(a, "approved")} onReject={(a) => setStatus(a, "rejected")}
              onPublish={(p, a) => setConfirmPublish({ pick: p, article: a })} />
          )}
        </div>
      )}

      {/* Quick preview drawer */}
      <Sheet open={!!previewArticle} onOpenChange={(o) => !o && setPreviewArticle(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {previewArticle && <QuickPreview article={previewArticle} allArticles={articles}
            onEdit={() => { setOpenArticle(previewArticle); setPreviewArticle(null); }} />}
        </SheetContent>
      </Sheet>

      {/* Editor sheet */}
      <Sheet open={!!openArticle} onOpenChange={(o) => !o && setOpenArticle(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          {openArticle && (
            <ArticleEditor article={openArticle} onSave={(u) => saveEdits(openArticle, u)} onClose={() => setOpenArticle(null)} />
          )}
        </SheetContent>
      </Sheet>

      {/* Confirm publish */}
      <Dialog open={!!confirmPublish} onOpenChange={(o) => !o && setConfirmPublish(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish pick + article?</DialogTitle>
            <DialogDescription>
              This will publish both the pick and its article in a single transaction.
            </DialogDescription>
          </DialogHeader>
          {confirmPublish && (
            <div className="te-card p-3 text-sm space-y-1">
              <div className="font-semibold">{confirmPublish.pick.title}</div>
              <div className="text-muted-foreground text-xs">{confirmPublish.pick.teams}</div>
              <div className="text-xs">Article: {confirmPublish.article.title}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmPublish(null)}>Cancel</Button>
            <Button onClick={() => confirmPublish && publishPair(confirmPublish.pick, confirmPublish.article)} disabled={!!busyId}>
              {busyId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Workflow group ----------------
const TONE: Record<string, { ring: string; text: string; bg: string }> = {
  primary: { ring: "border-primary/30", text: "text-primary", bg: "bg-primary/10" },
  accent: { ring: "border-accent/30", text: "text-accent", bg: "bg-accent/10" },
  destructive: { ring: "border-destructive/40", text: "text-destructive", bg: "bg-destructive/10" },
  muted: { ring: "border-border", text: "text-muted-foreground", bg: "bg-muted/40" },
};

function WorkflowGroup(props: {
  title: string; tone: keyof typeof TONE; rows: Row[]; icon: any; defaultOpen?: boolean;
  allArticles: Article[]; selected: Set<string>; busyId: string | null;
  onToggle: (id: string) => void; onToggleAll: (rows: Row[]) => void;
  onGenerate: (p: Pick) => void; onPreview: (a: Article) => void; onEdit: (a: Article) => void;
  onApprove: (a: Article) => void; onReject: (a: Article) => void; onPublish: (p: Pick, a: Article) => void;
}) {
  const { title, tone, rows, icon: Icon, defaultOpen, allArticles, selected, busyId,
    onToggle, onToggleAll, onGenerate, onPreview, onEdit, onApprove, onReject, onPublish } = props;
  const [open, setOpen] = useState(defaultOpen ?? false);
  const t = TONE[tone];
  if (rows.length === 0 && !defaultOpen) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={`te-card border ${t.ring} overflow-hidden`}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors">
            <span className={`w-7 h-7 rounded-md grid place-items-center ${t.bg}`}>
              <Icon className={`w-4 h-4 ${t.text}`} />
            </span>
            <span className="font-semibold">{title}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${t.bg} ${t.text}`}>{rows.length}</span>
            <ChevronDown className={`ml-auto w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          {rows.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">Nothing here. ✨</div>
          ) : (
            <div className="border-t border-border/60">
              <div className="px-4 py-2 flex items-center gap-2 bg-muted/20 border-b border-border/40">
                <Checkbox
                  checked={rows.every((r) => selected.has(r.article?.id ?? r.pick.id))}
                  onCheckedChange={() => onToggleAll(rows)}
                />
                <span className="text-[11px] text-muted-foreground uppercase tracking-wider">Select all</span>
              </div>
              <ul className="divide-y divide-border/40">
                {rows.map((r) => (
                  <CompactRow key={r.pick.id} row={r} allArticles={allArticles}
                    selected={selected.has(r.article?.id ?? r.pick.id)}
                    onToggle={() => onToggle(r.article?.id ?? r.pick.id)}
                    busy={busyId === r.pick.id || (r.article ? busyId === r.article.id : false)}
                    onGenerate={() => onGenerate(r.pick)} onPreview={() => r.article && onPreview(r.article)}
                    onEdit={() => r.article && onEdit(r.article)}
                    onApprove={() => r.article && onApprove(r.article)} onReject={() => r.article && onReject(r.article)}
                    onPublish={() => r.article && onPublish(r.pick, r.article)} />
                ))}
              </ul>
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ---------------- Compact row ----------------
function CompactRow({
  row, allArticles, selected, busy,
  onToggle, onGenerate, onPreview, onEdit, onApprove, onReject, onPublish,
}: {
  row: Row; allArticles: Article[]; selected: boolean; busy: boolean;
  onToggle: () => void; onGenerate: () => void; onPreview: () => void; onEdit: () => void;
  onApprove: () => void; onReject: () => void; onPublish: () => void;
}) {
  const { pick, article } = row;
  const hasArticle = !!article;
  const hasValidation = !!article?.validation_error;
  const seo = computeSeo(article, allArticles);

  return (
    <li className="px-4 py-2.5 hover:bg-muted/30 transition-colors group">
      <div className="flex items-center gap-3">
        <Checkbox checked={selected} onCheckedChange={onToggle} className="shrink-0" />

        {/* Confidence badge */}
        <div className="hidden sm:flex flex-col items-center justify-center w-11 h-11 rounded-lg bg-gradient-to-br from-primary/15 to-accent/10 border border-border/60 shrink-0">
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground leading-none">Conf</span>
          <span className="text-sm font-bold text-foreground leading-tight">{pick.confidence_score}</span>
        </div>

        {/* Main */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest h-4 px-1.5 border-primary/30 text-primary/90">{pick.sports?.code ?? "—"}</Badge>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest h-4 px-1.5">{pick.bet_type}</Badge>
            <StatusBadge status={article?.status ?? "none"} hasValidation={hasValidation} />
            <span className="text-[10px] text-muted-foreground">Pick: {pick.status}</span>
          </div>
          <div className="font-semibold text-sm truncate">{pick.title}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            <span className="truncate max-w-[40ch]">{pick.teams ?? "—"}</span>
            <span>· Game {fmtDate(pick.game_start_time)}</span>
            {article && <span>· Gen {fmtDate(article.ai_generated_at ?? article.created_at)} {fmtTime(article.ai_generated_at ?? article.created_at)}</span>}
            {article?.published_at && <span>· Pub {fmtDate(article.published_at)}</span>}
          </div>
          {article && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <code className="text-[10px] px-1.5 py-0.5 rounded bg-muted/60 text-foreground/70 truncate max-w-[40ch]">/{article.slug}</code>
              <span className="text-[10px] text-muted-foreground">{seo.words}w · {seo.readMin}m read</span>
              {seo.checks.map((c) => <SeoChip key={c.key} check={c} />)}
              <span className={`text-[10px] font-bold ml-1 ${seo.band.tone}`} title={`${seo.band.label} — ${seo.okCount} ok, ${seo.warnCount} warn, ${seo.failCount} fail`}>
                SEO {seo.score} · {seo.band.label}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {!hasArticle ? (
            <Button size="sm" onClick={onGenerate} disabled={busy}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate
            </Button>
          ) : (
            <>
              {article!.status === "approved" && (
                <Button size="sm" onClick={onPublish} disabled={busy}>
                  <Send className="w-3.5 h-3.5" /> Publish
                </Button>
              )}
              {article!.status !== "approved" && article!.status !== "published" && (
                <Button size="sm" variant="secondary" onClick={onApprove} disabled={busy || hasValidation}>
                  <ShieldCheck className="w-3.5 h-3.5" /> Approve
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={onPreview} title="Quick preview"><Eye className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={onEdit} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={onGenerate} disabled={busy} title="Regenerate">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
              {article!.status !== "rejected" && article!.status !== "published" && (
                <Button size="sm" variant="ghost" onClick={onReject} disabled={busy} className="text-destructive hover:text-destructive" title="Reject">
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
              {article!.status === "published" && (
                <Button size="sm" variant="ghost" asChild title="View live">
                  <Link to={`/${article!.slug}`}><ExternalLink className="w-3.5 h-3.5" /></Link>
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      {hasValidation && (
        <div className="mt-2 ml-8 text-[11px] text-destructive/90 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="line-clamp-2">{article!.validation_error}</span>
        </div>
      )}
    </li>
  );
}

// ---------------- Quick preview drawer ----------------
function QuickPreview({ article, allArticles, onEdit }: { article: Article; allArticles: Article[]; onEdit: () => void }) {
  const seo = computeSeo(article, allArticles);
  const lockedMatch = (article.article_body_md ?? "").match(/##\s*Locked Premium Section[\s\S]*?(?=\n##\s|$)/i);
  return (
    <div className="space-y-4">
      <SheetHeader>
        <div className="flex items-center justify-between gap-2">
          <SheetTitle className="truncate pr-6 text-base">{article.title}</SheetTitle>
          <StatusBadge status={article.status} hasValidation={!!article.validation_error} />
        </div>
        <code className="text-[11px] text-muted-foreground truncate">/{article.slug}</code>
      </SheetHeader>

      <div className="te-card p-3 space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Search snippet</div>
        <div className="text-primary text-sm truncate">{article.meta_title || article.title}</div>
        <div className="text-[11px] text-emerald-400/70 truncate">aleaedge.com/{article.slug}</div>
        <div className="text-xs text-muted-foreground line-clamp-2">{article.meta_description || article.excerpt || "—"}</div>
      </div>

      <div className="te-card p-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">SEO readiness</div>
          <div className={`text-sm font-bold ${seo.band.tone}`}>{seo.score}/100 · {seo.band.label}</div>
        </div>
        <Progress value={seo.score} className="h-1.5" />
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{seo.okCount} pass</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-accent" />{seo.warnCount} warn</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-destructive" />{seo.failCount} fail</span>
          <span className="ml-auto">{seo.words}w · {seo.readMin}m read · {seo.internalLinks} internal links</span>
        </div>
        <div className="grid grid-cols-1 gap-1.5">
          {seo.checks.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-2 text-xs px-2 py-1.5 rounded bg-muted/30 border border-border/40">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 font-medium">
                  <span className={`w-1.5 h-1.5 rounded-full ${SEO_DOT[c.level]}`} />{c.label}
                  <span className="text-[10px] text-muted-foreground font-normal">· {c.hint}</span>
                </div>
                {c.level !== "ok" && c.fix && (
                  <div className="text-[10px] text-muted-foreground/80 mt-0.5 leading-snug">{c.fix}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {article.excerpt && (
        <div className="te-card p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Teaser</div>
          <p className="text-sm">{article.excerpt}</p>
        </div>
      )}

      {lockedMatch && (
        <div className="te-card p-3 border border-accent/30">
          <div className="text-[10px] uppercase tracking-widest text-accent mb-1.5">Locked premium section</div>
          <article className="prose prose-invert prose-sm max-w-none">
            <ReactMarkdown>{lockedMatch[0]}</ReactMarkdown>
          </article>
        </div>
      )}

      <div className="sticky bottom-0 -mx-6 px-6 py-3 border-t border-border bg-background/80 backdrop-blur flex justify-end gap-2">
        <Button onClick={onEdit}><Pencil className="w-4 h-4" /> Open editor</Button>
      </div>
    </div>
  );
}

// ---------------- Empty / Editor (preserved) ----------------
function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <Card className="te-card border-0">
      <CardContent className="py-12 text-center">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center mb-3">
          <FileText className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-base font-semibold">No articles match your filters</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
          Generate your first article from the Admin → Picks console, or adjust the filters above.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="outline" onClick={onRefresh}><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button asChild><Link to="/admin"><Sparkles className="w-4 h-4" /> Go to picks</Link></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ArticleEditor({
  article, onSave, onClose,
}: { article: Article; onSave: (u: Partial<Article>) => void; onClose: () => void }) {
  const [title, setTitle] = useState(article.title);
  const [metaTitle, setMetaTitle] = useState(article.meta_title ?? "");
  const [metaDesc, setMetaDesc] = useState(article.meta_description ?? "");
  const [excerpt, setExcerpt] = useState(article.excerpt ?? "");
  const [body, setBody] = useState(article.article_body_md ?? "");

  useEffect(() => {
    setTitle(article.title);
    setMetaTitle(article.meta_title ?? "");
    setMetaDesc(article.meta_description ?? "");
    setExcerpt(article.excerpt ?? "");
    setBody(article.article_body_md ?? "");
  }, [article.id]);

  return (
    <div className="space-y-4">
      <SheetHeader>
        <div className="flex items-center justify-between gap-2">
          <SheetTitle className="truncate pr-6">{article.title}</SheetTitle>
          <StatusBadge status={article.status} hasValidation={!!article.validation_error} />
        </div>
        <div className="text-xs text-muted-foreground truncate">/{article.slug}</div>
      </SheetHeader>

      {article.validation_error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          <div className="flex items-center gap-2 font-semibold mb-1">
            <AlertTriangle className="w-4 h-4" /> Validation issues
          </div>
          <ul className="list-disc list-inside space-y-0.5">
            {article.validation_error.split(";").map((e, i) => <li key={i}>{e.trim()}</li>)}
          </ul>
        </div>
      )}

      <Tabs defaultValue="edit" className="w-full">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="space-y-3">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Excerpt"><Textarea rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} /></Field>
          <Field label="Article body (Markdown)">
            <Textarea rows={18} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-xs" />
          </Field>
        </TabsContent>

        <TabsContent value="preview">
          <div className="te-card p-5">
            <article className="prose prose-invert prose-sm max-w-none prose-headings:font-display prose-headings:tracking-tight">
              <ReactMarkdown>{body || "_No content_"}</ReactMarkdown>
            </article>
          </div>
        </TabsContent>

        <TabsContent value="seo" className="space-y-3">
          <Field label={`Meta title (${metaTitle.length}/60)`}>
            <Input maxLength={60} value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} />
          </Field>
          <Field label={`Meta description (${metaDesc.length}/160)`}>
            <Textarea rows={3} maxLength={160} value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} />
          </Field>
          <div className="te-card p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Search preview</div>
            <div>
              <div className="text-primary text-base truncate">{metaTitle || title}</div>
              <div className="text-xs text-emerald-400/70 truncate">aleaedge.com/{article.slug}</div>
              <div className="text-sm text-muted-foreground line-clamp-2">{metaDesc || excerpt}</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="sticky bottom-0 -mx-6 px-6 py-3 border-t border-border bg-background/80 backdrop-blur flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button onClick={() => onSave({
          title, meta_title: metaTitle, meta_description: metaDesc, excerpt, article_body_md: body,
        })}>Save changes</Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-muted-foreground mb-1.5">{label}</div>
      {children}
    </label>
  );
}
